import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import React from 'react';
import { useIsDesktop } from '../../../hooks/useIsDeskTop';
import { StyleSheet, Text, TextStyle, View } from 'react-native';
import { COLORS, FONTS } from '../../../constants';
import RadioButton from '../../../components/RadioButton/RadioButton';
import { TextInput } from '../../../components/TextInput/TextInput';
import { Button } from '../../../components/Button';
import DatetimeInfo from './DatetimeInfo';
import ProductTable from './ProductTable';
import { STYLE_VARS } from '../../../constants/style-vars';
import {
  CampPriceData,
  IBankData,
  ICamp,
  IPaymentMethod,
} from '../../../types/camp.types';
import { IBookingState } from '../types';
import { IFormData } from '../../../components/FormHelper/FormHelper.types';
import CampApi from '../../../services/api/camp.api';
import { Loading } from '../../../components';
import { convertToFullPhone } from '../../../services/utils/phoneConvert';
import { usePaymentSheet } from '@stripe/stripe-react-native';
import { IS_WEB } from '../../../constants/platform';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../services/redux';
import {
  deleteItem,
  resetCart,
  updateCartItemData,
} from '../../../services/redux/reducers/CartSlice';
import { PAYMENT_METHOD } from '../constants';
import { Alert } from '../../../services/alert';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import PaymentForm from './PaymentForm';

type Step3Props = {
  onSubmit?: (paymentMethod: number) => void;
  booking: ICamp;
  state: IBookingState;
  setState: (state: Partial<IBookingState>) => void;
  handleSetStep: (step: number) => void;
  paymentMethods: IPaymentMethod[];
  bankTransferData: IBankData;
  cartId: string;
};

const stripePromise = loadStripe(
  'pk_test_51M3tL9BegDGVD02DD79V7L0hpTMZjIlLPJRSBH9DCbLjwYmBylnr39e5d6qM4eBLUZRh2k7FUuHHUKMzY6kZ18NZ00TDoz3HH3'
);

const Step3: FC<Step3Props> = ({
  onSubmit,
  booking,
  state,
  setState,
  handleSetStep,
  paymentMethods,
  bankTransferData,
  cartId,
}) => {
  const isDesktop = useIsDesktop();
  const dispatch = useDispatch();
  const [paymentMethod, setPaymentMethod] = useState<IPaymentMethod>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [priceData, setPriceData] = useState<CampPriceData>({
    booking_price: 0,
    total: 0,
    room_price: 0,
    rooms: [],
    additional_bed_price: 0,
    dishes_price: 0,
    camp_price: 0,
    price_type: 'regular',
    dishes: [],
  });
  {
    /* Disable on website */
  }
  // const { initPaymentSheet, presentPaymentSheet, loading } = usePaymentSheet();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const getCalculatePrice = useCallback(async () => {
    setIsLoading(true);
    const formData: IFormData = {
      camp_id: booking.id,
      rooms: state.rooms,
      start_time: `${state.start_date}`,
      end_time: `${state.end_date}`,
      additional_bed: 1,
      adult: state.adult,
      elderly: state.elderly,
      is_vegetarian: state.is_vegetarian ? 1 : 0,
      dinner_meal: {
        name: state.dinner_meal?.name,
      },
      dishes: state.extra_dishes.map((item) => {
        return {
          id: item.dish.id,
          quantity: item.quantity,
          date: item.date,
          type: item.type,
        };
      }),
      activities: state.extra_activities.map((item) => {
        return {
          id: item.activity.id,
          attendees: item.attendees,
          date: item.date,
        };
      }),
    };

    if (!state.dinner_meal) {
      delete formData.dinner_meal;
    }

    CampApi.postCalculatePrice(formData)
      .then((res) => {
        setPriceData(res.data.data);
        setState({
          price_data: res.data.data,
        });
        dispatch(
          updateCartItemData({
            id: cartId,
            data: {
              price_data: res.data.data,
            },
            step: 3,
          })
        );
      })
      .catch((error) => {
        Alert.alert('Error', error.meta.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [
    dispatch,
    cartId,
    booking,
    setState,
    state.adult,
    state.dinner_meal,
    state.elderly,
    state.end_date,
    state.extra_dishes,
    state.is_vegetarian,
    state.start_date,
    state.rooms,
    state.extra_activities,
  ]);

  const renderEmergencyContact = useMemo(() => {
    return (
      <View
        style={{
          backgroundColor: '#f9f2f1',
          padding: 15,
          borderRadius: 18,
          flexDirection: 'column',
          marginBottom: 25,
        }}
      >
        <Text style={[styles.tableHeaderText, { marginBottom: 17 }]}>
          Emergency Contact
        </Text>
        <View
          style={[
            isDesktop()
              ? { flexDirection: 'row' }
              : { flexDirection: 'column' },
          ]}
        >
          <View
            style={[
              isDesktop()
                ? { width: '30%' }
                : { width: '100%', marginBottom: 15 },
            ]}
          >
            <Text
              style={styles.tableContentText}
            >{`Name: ${state.emergency_first_name} ${state.emergency_last_name}`}</Text>
          </View>
          <View
            style={[
              isDesktop()
                ? { width: '45%' }
                : { width: '100%', marginBottom: 15 },
            ]}
          >
            <Text style={styles.tableContentText}>
              {`Phone Number: ${convertToFullPhone(
                state.emergency_phone_country,
                state.emergency_phone_number
              )}`}
            </Text>
          </View>
          <View style={[isDesktop() ? { width: '25%' } : { width: '100%' }]}>
            <Text
              style={styles.tableContentText}
            >{`Relationship: ${state.emergency_relationship}`}</Text>
          </View>
        </View>
      </View>
    );
  }, [
    isDesktop,
    state.emergency_first_name,
    state.emergency_last_name,
    state.emergency_phone_country,
    state.emergency_phone_number,
    state.emergency_relationship,
  ]);

  useEffect(() => {
    getCalculatePrice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const paymentMethodOptions = useMemo(() => {
    return paymentMethods.map((item) => {
      return {
        label: item.name,
        value: item.id,
      };
    });
  }, [paymentMethods]);

  const onCreateBooking = useCallback(async () => {
    try {
      setIsSubmitting(true);
      const formData: IFormData = {
        camp_id: booking.id,
        payment_method_id: paymentMethod?.id,
        rooms: state.rooms,
        start_time: `${state.start_date}`,
        end_time: `${state.end_date}`,
        adult: state.adult,
        elderly: state.elderly,
        is_vegetarian: state.is_vegetarian ? 1 : 0,
        dinner_meal: {
          name: state.dinner_meal?.name,
        },
        emergency_contact: {
          first_name: state.emergency_first_name,
          last_name: state.emergency_last_name,
          phone: state.emergency_phone_number,
          phone_country: state.emergency_phone_country,
          relationship: state.emergency_relationship,
        },
        dishes: state.extra_dishes.map((item) => {
          return {
            id: item.dish.id,
            quantity: item.quantity,
            date: item.date,
            type: item.type,
          };
        }),
        activities: state.extra_activities.map((item) => {
          return {
            id: item.activity.id,
            attendees: item.attendees,
            date: item.date,
          };
        }),
      };

      const response = await CampApi.postCreateCampBooking(formData);

      if (!response.data.meta.success) {
      } else {
        setState({
          booking_id: response.data.data.id,
        });

        if (paymentMethod?.id === PAYMENT_METHOD.CREDIT_CARD) {
          if (!IS_WEB && response.data.data.payment_intent) {
            {
              /* Disable on website */
            }
            // const initRes = await initPaymentSheet({
            //   merchantDisplayName: 'Booking camp',
            //   paymentIntentClientSecret:
            //     response.data.data.payment_intent.client_secret,
            //   customerId: response.data.data.payment_intent.customer,
            // });
            // if (initRes.error) {
            //   Alert.alert('Error', initRes.error.message);
            // } else {
            //   setIsSubmitting(false);
            //   const presentRes = await presentPaymentSheet();
            //   if (presentRes.error) {
            //     Alert.alert('Error', presentRes.error.message);
            //   } else {
            //     dispatch(deleteItem(cartId));
            //     if (onSubmit) onSubmit(paymentMethod.id);
            //   }
            // }
          } else {
            // const { payment_intent } = response.data.data;

            // if (!payment_intent) {
            //   Alert.alert('Error', 'Payment intent not found.');
            //   return;
            // }

            // const stripe = await stripePromise;
            // const { error } = await stripe!.confirmCardPayment(
            //   payment_intent.client_secret
            // );

            // if (error) {
            //   Alert.alert('Error', error.message);
            // } else {
            dispatch(deleteItem(cartId));
            if (onSubmit) onSubmit(paymentMethod.id);
            // }
          }
        } else if (paymentMethod?.id === PAYMENT_METHOD.BANK_TRANSFER) {
          dispatch(deleteItem(cartId));
          setIsSubmitting(false);
          if (onSubmit) onSubmit(paymentMethod.id);
        }
      }
    } catch (e: any) {
      Alert.alert('Error', e.meta.message);
      setIsSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    paymentMethod,
    booking,
    onSubmit,
    setState,
    state,
    setIsSubmitting,
    dispatch,
  ]);

  return (
    <View
      style={[
        {
          borderRadius: 18,
          width: '100%',
        },
        isDesktop()
          ? {
              elevation: 5,
              shadowColor: '#F0F0F0',
              shadowOffset: { width: 0, height: 12 },
              shadowOpacity: 1,
              shadowRadius: 32,
              paddingTop: 25,
              paddingHorizontal: 25,
              marginBottom: 150,
            }
          : {
              backgroundColor: COLORS.WHITE,
              paddingTop: 30,
              paddingHorizontal: 20,
            },
      ]}
    >
      {!isLoading && (
        <>
          <DatetimeInfo state={state} booking={booking} />
          <ProductTable
            state={state}
            booking={booking}
            priceData={priceData}
            setState={setState}
            editable={true}
          />
          {renderEmergencyContact}
          <View>
            <View>
              <Text style={[styles.tableHeaderText, { marginBottom: 10 }]}>
                Payment
              </Text>
              <RadioButton
                options={paymentMethodOptions}
                selectedOption={paymentMethod?.id}
                onSelect={(value) => {
                  const method = paymentMethods.find((pm) => pm.id === value);
                  setPaymentMethod(method);
                }}
                optionStyle={{
                  marginRight: isDesktop() ? 100 : 0,
                  marginBottom: isDesktop() ? 0 : 5,
                }}
                radioStyle={{
                  marginBottom: isDesktop() ? 20 : 5,
                  flexDirection: isDesktop() ? 'row' : 'column',
                  alignItems: isDesktop() ? 'center' : 'flex-start',
                }}
              />
            </View>
            <View style={{ marginBottom: 30 }}>
              {paymentMethod && paymentMethod.name === 'Bank Transfer' && (
                <Text
                  style={{
                    color: COLORS.BLACK,
                    fontSize: 16,
                    fontFamily: FONTS.AvenirRegular,
                    fontWeight: '500',
                  }}
                >
                  {`After clicking Pay Now, you will need to complete bank transfer to ${bankTransferData.bank_name} Account Number ${bankTransferData.account_number} and upload your receipt.`}
                </Text>
              )}
            </View>
            {/* <View
              style={[
                { marginBottom: 15 },
                isDesktop()
                  ? { flexDirection: 'row', flexWrap: 'wrap', gap: 20 }
                  : { flexDirection: 'column' },
              ]}
            >
              <View
                style={{
                  flex: 1,
                  flexDirection: 'row',
                  marginBottom: isDesktop() ? 0 : 15,
                }}
              >
                <Text
                  style={[
                    styles.inputText,
                    { width: isDesktop() ? '40%' : '50%' },
                  ]}
                >
                  Name of card:
                </Text>
                <View style={{ width: '50%' }}>
                  <TextInput style={STYLE_VARS.textInputMini as TextStyle} />
                </View>
              </View>
              <View
                style={{
                  flex: 1,
                  flexDirection: 'row',
                }}
              >
                <Text
                  style={[
                    styles.inputText,
                    { width: isDesktop() ? '40%' : '50%' },
                  ]}
                >
                  Card Number:
                </Text>
                <View style={{ width: '50%' }}>
                  <TextInput style={STYLE_VARS.textInputMini as TextStyle} />
                </View>
              </View>
            </View>
            <View style={{ marginBottom: 40 }}>
              <View
                style={{
                  flexDirection: 'row',
                  width: isDesktop() ? '50%' : '100%',
                }}
              >
                <View
                  style={{
                    width: '50%',
                    flexDirection: 'row',
                    alignItems: 'center',
                  }}
                >
                  <Text style={[styles.inputText, { width: '30%' }]}>CSV:</Text>
                  <View style={{ width: '40%' }}>
                    <TextInput style={STYLE_VARS.textInputMini as TextStyle} />
                  </View>
                </View>
                <View
                  style={{
                    width: '50%',
                    flexDirection: 'row',
                    alignItems: 'center',
                  }}
                >
                  <Text style={[styles.inputText, { width: '25%' }]}>EXP:</Text>
                  <View style={{ width: '25%' }}>
                    <TextInput style={STYLE_VARS.textInputMini as TextStyle} />
                  </View>
                  <Text
                    style={{
                      fontSize: 20,
                      marginHorizontal: 5,
                      fontWeight: '500',
                      color: COLORS.GREY,
                    }}
                  >
                    {'/'}
                  </Text>
                  <View style={{ width: '25%' }}>
                    <TextInput style={STYLE_VARS.textInputMini as TextStyle} />
                  </View>
                </View>
              </View>
            </View> */}
            {/* {IS_WEB && (
              <Elements stripe={stripePromise}>
                <PaymentForm
                  onSubmitSuccess={(paymentMethodId) => {
                    console.log('ok');
                  }}
                  onSubmitError={(error) => {
                    Alert.alert('Error', error);
                  }}
                />
              </Elements>
            )} */}
            <View
              style={{
                flexDirection: 'row',
                justifyContent: 'center',
              }}
            >
              {isSubmitting ? (
                <View style={{ marginVertical: 20 }}>
                  <Loading />
                </View>
              ) : (
                <Button
                  style={[
                    styles.buttonStyle,
                    !isDesktop() && { width: '100%', paddingVertical: 30 },
                    (isLoading || paymentMethod === undefined) && {
                      backgroundColor: '#de7e86',
                    },
                  ]}
                  className=""
                  arrowRight={false}
                  textStyle={[
                    styles.textButtonStyle,
                    !isDesktop() && { fontSize: 19 },
                  ]}
                  text="Pay now"
                  onPress={() => {
                    onCreateBooking();
                  }}
                  disabled={isLoading || paymentMethod === undefined}
                />
              )}
            </View>
          </View>
        </>
      )}
      {isLoading && (
        <View style={{ marginBottom: 25 }}>
          <Loading />
        </View>
      )}
      {/* {isSubmitting && <Loading isVisible={isSubmitting} isFullScreen />} */}
    </View>
  );
};

export default Step3;

const styles = StyleSheet.create({
  tableHeaderText: {
    fontFamily: FONTS.AvenirMedium,
    fontWeight: '800',
    fontSize: 19,
    color: COLORS.BLACK,
  },
  tableContentText: {
    fontFamily: FONTS.AvenirMedium,
    fontWeight: '500',
    fontSize: 16,
    color: COLORS.GREY,
    paddingRight: 5,
  },
  inputText: {
    fontFamily: FONTS.AvenirMedium,
    fontWeight: '500',
    fontSize: 16,
    color: COLORS.GREY,
  },
  input: {
    width: '100%',
    borderWidth: 1,
    borderColor: '#DDDDE3',
    borderStyle: 'solid',
    paddingHorizontal: 0,
    letterSpacing: 1.2,
    borderRadius: 4,
    fontSize: 16,
    paddingVertical: 5,
  },
  buttonStyle: {
    backgroundColor: COLORS.RED_PRIMARY,
    paddingVertical: 23,
    borderRadius: 13,
    width: 320,
    marginBottom: 20,
    overflow: 'hidden',
  },
  textButtonStyle: {
    color: COLORS.WHITE,
    fontSize: 17,
    fontFamily: FONTS.AvenirMedium,
    textTransform: 'uppercase',
    fontWeight: '500',
    letterSpacing: 1,
  },
});
